import { Bell, Bookmark, Bot, FileText, Home, MessageCircle, Moon, Plus, ShieldCheck, Headphones, Sun } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, Presence } from "./Avatar";
import { useAuth } from "@/lib/auth";
import { useChat } from "@/lib/chat";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import { useTheme } from "@/lib/theme";
import { analytics } from "@/lib/analytics";

const RailItem = ({ id, label, icon: Icon, active, badge, dot, onClick }) => (
  <button className={`tc-rail__item${active ? " is-active" : ""}`} onClick={onClick} data-testid={`rail-${id}`} aria-label={label}>
    <span className="tc-rail__icon">
      <Icon size={20} strokeWidth={active ? 2.4 : 2} />
      {badge ? <span className="tc-rail__badge" data-testid={`rail-${id}-badge`}>{badge > 99 ? "99+" : badge}</span> : dot ? <span className="tc-rail__dot" /> : null}
    </span>
    <span className="tc-rail__label">{label}</span>
  </button>
);

export const IconRail = ({ view }) => {
  const navigate = useNavigate();
  const { user, workspace, logout, setUser } = useAuth();
  const { setModal, me, can, totalMentions: mentionUnread, dmUnread } = useChat();
  const theme = useTheme();
  const lastHome = localStorage.getItem("tc_last_conv");

  const setPresence = async (presence) => {
    const { data } = await api.post("/users/me/presence", { presence });
    setUser(data);
    toast.success(presence === "away" ? "You're set to away" : "You're back to active");
  };

  return (
    <nav className="tc-rail" data-testid="icon-rail">
      <button className="tc-rail__ws" title={workspace?.name} onClick={() => navigate(lastHome ? `/client/${lastHome}` : "/client")} data-testid="rail-workspace-icon">
        {initials(workspace?.name || "Total Chat").slice(0, 3)}
      </button>
      <RailItem id="home" label="Home" icon={Home} active={view === "home"} dot={!!mentionUnread && view !== "home"} onClick={() => navigate(lastHome ? `/client/${lastHome}` : "/client")} />
      <RailItem id="dms" label="DMs" icon={MessageCircle} active={view === "dms"} badge={dmUnread} onClick={() => navigate("/client/dms")} />
      <RailItem id="activity" label="Activity" icon={Bell} active={view === "activity"} badge={mentionUnread} onClick={() => navigate("/client/activity")} />
      <RailItem id="later" label="Later" icon={Bookmark} active={view === "later"} onClick={() => navigate("/client/later")} />
      <RailItem id="files" label="Files" icon={FileText} active={view === "files"} onClick={() => navigate("/client/files")} />
      <RailItem id="huddles" label="Huddles" icon={Headphones} active={view === "huddles"} onClick={() => navigate("/client/huddles")} />
      <RailItem id="tools" label="Agents & tools" icon={Bot} active={view === "tools"} onClick={() => navigate("/client/tools")} />
      {can("admin.console") && (
        <>
          <span className="tc-rail__sep" />
          <RailItem id="admin" label="Admin" icon={ShieldCheck} active={view === "admin"} onClick={() => navigate("/admin")} />
        </>
      )}
      <div className="tc-rail__bottom">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="tc-rail__round" title="Create new" data-testid="rail-create-button"><Plus size={20} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="tc-menu" sideOffset={10}>
            <div className="tc-menu__head">Create</div>
            {can("dm.send") && <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "newMessage" })} data-testid="create-message-item">Message</DropdownMenuItem>}
            {(can("channels.create") || can("channels.create_private")) && <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "createChannel" })} data-testid="create-channel-item">Channel</DropdownMenuItem>}
            {can("huddles.start") && <DropdownMenuItem className="tc-menu__item" onClick={() => navigate("/client/huddles")} data-testid="create-huddle-item">Huddle</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
        <button className="tc-rail__round" title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} onClick={() => { analytics.track("theme_toggle_clicked", { category: "preferences", label: theme === "dark" ? "light" : "dark" }); setModal({ type: "preferences", tab: "appearance" }); }} data-testid="rail-theme-button">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="tc-rail__me" title={user?.display_name} data-testid="rail-user-menu-button">
              <Avatar user={user} size={36} />
              <Presence presence={user?.presence} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="tc-menu" sideOffset={10} style={{ minWidth: 300 }}>
            <div style={{ display: "flex", gap: 12, padding: "8px 24px 12px", alignItems: "center" }}>
              <Avatar user={user} size={44} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: 15 }}>{user?.display_name}</div>
                <div style={{ fontSize: 13, color: "var(--tc-text-muted)", display: "flex", alignItems: "center", gap: 4 }}><Presence presence={user?.presence} /> {user?.presence === "active" ? "Active" : "Away"}</div>
              </div>
            </div>
            <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "editProfile" })} data-testid="user-menu-status">Update your status</DropdownMenuItem>
            <DropdownMenuItem className="tc-menu__item" onClick={() => setPresence(user?.manual_presence === "away" ? "auto" : "away")} data-testid="user-menu-presence">
              Set yourself as <b style={{ marginLeft: 4 }}>{user?.manual_presence === "away" ? "active" : "away"}</b>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="tc-menu__sep" />
            <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "editProfile" })} data-testid="user-menu-profile">Profile</DropdownMenuItem>
            <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "preferences" })} data-testid="user-menu-preferences">Preferences</DropdownMenuItem>
            <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "profile", userId: user?.id })} data-testid="user-menu-email">
              <span style={{ color: "var(--tc-text-muted)", fontSize: 13 }}>{user?.chat_email}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="tc-menu__sep" />
            <DropdownMenuItem className="tc-menu__item" onClick={() => { logout(); navigate("/login"); }} data-testid="user-menu-signout">Sign out of {workspace?.name}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
};
